<template>
  <div class="actuals-filter-chips driver-actuals-filter-chips">
    <span
      class="actuals-chip actuals-chip-clickable actuals-chip-total"
      :class="{ 'actuals-chip-selected': selectedStatus == null }"
      @click="toggle(null)"
    >{{ itemsCount }}/{{ itemsFullCount }}</span>
    <span class="actuals-filter-sep">|</span>
    <span class="actuals-filter-label">Status</span>
    <span
      class="actuals-chip actuals-chip-clickable success"
      :class="{ 'actuals-chip-selected': selectedStatus === 'success' }"
      @click="toggle('success')"
    >Success {{ successCount }}</span>
    <span
      class="actuals-chip actuals-chip-clickable error"
      :class="{ 'actuals-chip-selected': selectedStatus === 'error' }"
      @click="toggle('error')"
    >Errors {{ errorCount }}</span>
    <span
      class="actuals-chip actuals-chip-clickable cancelled"
      :class="{ 'actuals-chip-selected': selectedStatus === 'cancelled' }"
      @click="toggle('cancelled')"
    >Cancelled {{ cancelledCount }}</span>
    <span
      class="actuals-chip actuals-chip-clickable unprocessed"
      :class="{ 'actuals-chip-selected': selectedStatus === 'unprocessed' }"
      @click="toggle('unprocessed')"
    >Unprocessed {{ unprocessedCount }}</span>
    <span
      v-if="showSkipped"
      class="actuals-chip actuals-chip-clickable skipped"
      :class="{ 'actuals-chip-selected': selectedStatus === 'skipped' }"
      @click="toggle('skipped')"
    >Skipped {{ skippedCount }}</span>
    <span class="actuals-filter-sep">|</span>
    <span
      class="actuals-chip actuals-chip-clickable"
      :class="[overlapChipClass, { 'actuals-chip-selected': selectedStatus === overlapFilterKey }]"
      :title="overlapTitle"
      @click="toggle(overlapFilterKey)"
    >Overlap {{ overlapCount }}</span>
    <template v-if="otherAssignmentCount > 0">
      <span class="actuals-filter-sep">|</span>
      <span
        class="actuals-chip actuals-chip-clickable actuals-other-assignment-chip"
        :class="{ 'actuals-chip-selected': showOtherAssignments }"
        title="Pre-trip, post-trip and non-operating blocks drawn on the timeline. Click to hide them; click again to bring them back."
        @click="$emit('toggle-other-assignments', !showOtherAssignments)"
      >Other Assignments {{ otherAssignmentCount }}</span>
    </template>
    <template v-if="bookingTypeChips.length > 1">
      <span class="actuals-filter-sep">|</span>
      <span class="actuals-filter-chip-group actuals-booking-type-filter-group">
        <span class="actuals-filter-label" :title="bookingTypeTooltips.group">Type</span>
        <span
          v-for="opt in bookingTypeChips"
          :key="opt.value"
          class="actuals-chip actuals-chip-clickable actuals-booking-type-chip"
          :class="[
            'booking-type-' + opt.value.replace('_', '-'),
            { 'actuals-chip-selected': selectedBookingType === opt.value }
          ]"
          :title="bookingTypeTooltips[opt.value]"
          @click="toggleBookingType(opt.value)"
        >{{ opt.label }} {{ opt.count }}</span>
      </span>
    </template>
  </div>
</template>

<script>
import {
  actualsBookingTypeFilters,
  actualsBookingTypeTooltips,
  countActualsBookingType,
} from '@/helpers/actuals_booking_type_filter'

export default {
  name: 'driver_actuals_filter_chips',

  props: {
    itemsFull: { type: Array, default: () => [] },
    itemsCount: { type: Number, default: 0 },
    selectedStatus: { default: null },
    showSkipped: { type: Boolean, default: false },
    overlapFilterKey: { type: String, default: 'driver_overlap' },
    overlapTitle: { type: String, default: 'Duty times overlap another booking' },
    overlapChipClass: { type: String, default: 'overlap-driver' },
    // booking type is a second, independent dimension - a page opts in by binding
    // selected-booking-type, and the chips only appear when the rows actually hold
    // more than one type, so a scheduled-service-only tenant sees nothing
    selectedBookingType: { default: null },
    bookingTypeSource: { type: String, default: 'tbn' },
    // the Other Assignment bars are an overlay rather than a row filter, so this
    // chip shows and hides them; pages that do not draw them pass no count
    otherAssignmentCount: { type: Number, default: 0 },
    showOtherAssignments: { type: Boolean, default: true },
  },

  computed: {
    itemsFullCount() {
      return (this.itemsFull || []).length
    },
    statusCounts() {
      const counts = {
        success: 0,
        error: 0,
        cancelled: 0,
        unprocessed: 0,
        skipped: 0,
        overlap: 0,
      }
      const overlapKey = this.overlapFilterKey
      for (const item of this.itemsFull || []) {
        if (item[overlapKey])
          counts.overlap++
        if (counts[item.status] !== undefined)
          counts[item.status]++
      }
      return counts
    },
    successCount() {
      return this.statusCounts.success
    },
    errorCount() {
      return this.statusCounts.error
    },
    cancelledCount() {
      return this.statusCounts.cancelled
    },
    unprocessedCount() {
      return this.statusCounts.unprocessed
    },
    skippedCount() {
      return this.statusCounts.skipped
    },
    overlapCount() {
      return this.statusCounts.overlap
    },
    bookingTypeTooltips() {
      return actualsBookingTypeTooltips(this.bookingTypeSource)
    },
    bookingTypeChips() {
      // counts come from the unfiltered rows, so they do not move as chips are
      // clicked; an empty type is dropped rather than shown as a zero
      return actualsBookingTypeFilters(this.bookingTypeSource)
        .map(opt => ({
          ...opt,
          count: countActualsBookingType(this.itemsFull, opt.value, this.bookingTypeSource),
        }))
        .filter(opt => opt.count > 0)
    },
  },

  methods: {
    toggle(status) {
      const next = (status == null || this.selectedStatus === status) ? null : status
      this.$emit('filter', next)
    },
    toggleBookingType(bookingType) {
      const next = (bookingType == null || this.selectedBookingType === bookingType) ? null : bookingType
      this.$emit('filter-booking-type', next)
    },
  },
}
</script>

<style>
.driver-actuals-filter-chips {
  margin: 12px 8px;
}
.driver-actuals-filter-chips .actuals-filter-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
}
.actuals-filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
}
.actuals-filter-sep {
  color: #adb5bd;
  margin: 0 2px;
}
.actuals-chip {
  display: inline-block;
  padding: 4px 10px;
  border: 1px solid #ccc;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 600;
  background-color: #f8f9fa;
  white-space: nowrap;
}
.actuals-chip-clickable {
  cursor: pointer;
}
.actuals-chip-clickable:hover {
  opacity: 0.88;
}
.actuals-chip-selected:not(.actuals-chip-total) {
  outline: 2px solid #495057;
  outline-offset: 1px;
}
.actuals-chip-total.actuals-chip-selected {
  border: 1px solid #ccc !important;
  box-shadow: none !important;
  outline: none;
}
.actuals-chip.success { background-color: hsla(120, 100%, 75%, 0.55); border-color: #7dcc7d; }
.actuals-chip.error { background-color: rgba(255, 0, 0, 0.2); border-color: #e88; }
.actuals-chip.unprocessed { background-color: hsla(200, 40%, 55%, 0.55); border-color: #5f7d8f; }
.actuals-chip.skipped { background-color: hsla(190, 90%, 80%, 0.55); border-color: #8cc; }
.actuals-chip.cancelled { background-color: hsla(290, 60%, 70%, 0.45); border-color: #b9b; }
.actuals-chip.overlap-driver { background-color: hsla(35, 100%, 70%, 0.65); border-color: #e6a23c; color: #7c4a03; }
.actuals-chip.overlap-vehicle { background-color: hsla(35, 100%, 70%, 0.65); border-color: #e6a23c; color: #7c4a03; }
</style>
